export const formatId = (id, type) => {
  try {
    if (!id) return null;
    if (typeof id === "string" && id.includes("_")) return id;
    if (typeof id === "string" && id.includes("-")) return id;
    return `${type}_${id}`;
  } catch (error) {
    console.error("An error occurred while formatting ID: ", error);
  }
};

export const stripPrefix = (prefixedId) => {
  try {
    if (!prefixedId) return null;
    if (typeof prefixedId === "number") return prefixedId;
    if (typeof prefixedId !== "string") return prefixedId;
    if (prefixedId.includes("-")) return prefixedId;
    
    const parts = prefixedId.split("_");
    if (parts.length > 1) {
      return parseInt(parts[1], 10);
    }
    
    const parsed = parseInt(prefixedId, 10);
    return isNaN(parsed) ? prefixedId : parsed;
  } catch (error) {
    console.error("An error occurred while stripping prefix: ", error);
  }
};

const transformUser = (user) => {
  try {
    if (!user) return null;
    return {
      ...user,
      id: formatId(user.id, "user"),
    };
  } catch (error) {
    console.error("An error occurred while transforming user: ", error);
  }
};

const transformMember = (member) => {
  try {
    if (!member) return null;
    return {
      ...member,
      id: formatId(member.id, "user_group"),
      user_id: formatId(member.user_id, "user"),
      group_id: formatId(member.group_id, "group"),
      user: member.user ? transformUser(member.user) : undefined,
    };
  } catch (error) {
    console.error("An error occurred while transforming member: ", error);
  }
};

const transformMessage = (message) => {
  if (!message) return null;

  const recipientType = message.recipient_type === 0 ? "group" : "user";

  return {
    ...message,
    id: message.id,
    thread_id: message.thread_id,
    workspace_id: formatId(message.workspace_id, "workspace"),
    recipient_id: formatId(message.recipient_id, recipientType),
    user_id: formatId(message.user_id, "user"),
    sender: message.sender ? transformUser(message.sender) : undefined,
    mentions: message.mentions
      ? message.mentions.map((id) => formatId(id, "user"))
      : [],
    mention_groups: message.mention_groups
      ? message.mention_groups.map((id) => formatId(id, "group"))
      : [],
    mentioned_users: message.mentioned_users
      ? message.mentioned_users.map(transformUser)
      : undefined,
  };
};

const transformGroup = (group) => {
  if (!group) return null;
  return {
    ...group,
    id: formatId(group.id, "group"),
    workspace_id: formatId(group.workspace_id, "workspace"),
    created_by: formatId(group.created_by, "user"),
    creator: group.creator ? transformUser(group.creator) : undefined,
    members: group.members ? group.members.map(transformMember) : undefined,
  };
};

const transformThread = (thread) => {
  if (!thread) return null;

  const recipientType = thread.recipient_type === 0 ? "group" : "user";

  return {
    ...thread,
    id: thread.id,
    workspace_id: formatId(thread.workspace_id, "workspace"),
    recipient_id: formatId(thread.recipient_id, recipientType),
    root_message_id: thread.root_message_id,
    root_user_id: formatId(thread.root_user_id, "user"),
    root_user: thread.root_user ? transformUser(thread.root_user) : undefined,
  };
};

export const transformResponse = (data) => {
  if (!data) return data;

  const transformed = { ...data };

  if (data.group) {
    transformed.group = transformGroup(data.group);
  }

  if (data.groups) {
    transformed.groups = data.groups.map(transformGroup);
  }

  if (data.message) {
    transformed.message = transformMessage(data.message);
  }

  if (data.messages) {
    transformed.messages = data.messages.map(transformMessage);
  }

  if (data.member) {
    transformed.member = transformMember(data.member);
  }

  if (data.members) {
    transformed.members = data.members.map(transformMember);
  }

  if (data.user_group) {
    transformed.user_group = transformMember(data.user_group);
  }

  if (data.thread) {
    transformed.thread = transformThread(data.thread);
  }

  if (data.threads) {
    transformed.threads = data.threads.map(transformThread);
  }

  return transformed;
};

export const transformRequest = (payload) => {
  if (!payload) return payload;

  const transformed = { ...payload };

  if (payload.user_id) {
    transformed.user_id = stripPrefix(payload.user_id);
  }

  if (payload.group_id) {
    transformed.group_id = stripPrefix(payload.group_id);
  }

  if (payload.recipient_id) {
    transformed.recipient_id = stripPrefix(payload.recipient_id);
  }

  if (payload.workspace_id) {
    transformed.workspace_id = stripPrefix(payload.workspace_id);
  }

  if (payload.root_user_id) {
    transformed.root_user_id = stripPrefix(payload.root_user_id);
  }

  if (payload.mentions && Array.isArray(payload.mentions)) {
    transformed.mentions = payload.mentions.map(stripPrefix);
  }

  if (payload.mention_groups && Array.isArray(payload.mention_groups)) {
    transformed.mention_groups = payload.mention_groups.map(stripPrefix);
  }

  return transformed;
};

export const formatNamesList = (users, renderFn, maxNames = 2) => {
  try {
    if (!users || users.length === 0) return "";
    if (users.length === 1) return renderFn(users[0]);
    if (users.length <= maxNames) return users.map(renderFn).join(", ");
    
    const displayedNames = users.slice(0, maxNames).map(renderFn).join(", ");
    const remaining = users.length - maxNames;
    return `${displayedNames}, ${remaining} ${remaining === 1 ? 'other' : 'others'}`;
  } catch (error) {
    console.error("An error occurred while formatting names list: ", error);
    return users.map(renderFn).join(", ");
  }
};

export const formatAttachmentsForAPI = (attachments) => {
  if (!attachments || attachments.length === 0) return [];

  return attachments
    .map((attachment) => {
      const payload = {
        url: attachment.url,
        media_type: attachment.content_type || "application/octet-stream",
      };

      if (attachment.file_name) {
        payload.file_name = attachment.file_name;
      }

      return payload;
    })
    .filter((a) => a.url);
};

export const formatAttachmentsForDisplay = (attachments) => {
  if (!attachments || attachments.length === 0) return [];
  
  return attachments.map((attachment, index) => {
    if (typeof attachment === 'string') {
      const url = attachment;
      const fileName = url.split('/').pop() || `attachment_${index + 1}`;
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      
      let contentType = 'application/octet-stream';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
        contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
      } else if (['mp4', 'webm', 'ogv'].includes(extension)) {
        contentType = `video/${extension}`;
      } else if (['mp3', 'wav', 'ogg'].includes(extension)) {
        contentType = `audio/${extension}`;
      } else if (extension === 'pdf') {
        contentType = 'application/pdf';
      }
      
      return {
        id: `${index}_${fileName}`,
        url: url,
        file_name: fileName,
        content_type: contentType,
      };
    } else if (attachment && typeof attachment === 'object') {
      return {
        ...attachment,
        id: attachment.id || `${index}_${attachment.file_name || 'attachment'}`,
        url: attachment.url || attachment.file_url,
        name: attachment.name || attachment.file_name || 'Attachment',
        file_name: attachment.file_name,
        content_type: attachment.media_type || attachment.content_type || 'application/octet-stream',
      };
    }
    
    return null;
  }).filter(Boolean);
};

export const createPlaceholderDM = (user, renderEntityFn) => {
  const name = renderEntityFn ? renderEntityFn(user) : user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return {
    id: `user_${user.id}`,
    is_placeholder: true,
    is_dm: true,
    type: "dm",
    recipient_type: 1,
    name,
    other_user: user,
  };
};

export const createPlaceholderGroupDM = (users, renderEntityFn) => {
  const names = users.map((u) => renderEntityFn ? renderEntityFn(u) : u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim());
  const name = names.join(", ");
  const userIds = users.map((u) => u.id);
  return {
    id: `group_dm_${userIds.join("_")}`,
    is_placeholder: true,
    is_dm: true,
    type: "group_dm",
    recipient_type: 2,
    name,
    placeholder_users: users,
  };
};

export const downloadAttachment = async (attachment, onError) => {
  try {
    if (!attachment?.url) {
      if (onError) {
        onError("Attachment URL not found");
      }
      return;
    }

    const fileName = attachment.file_name || attachment.name || 'download';
    
    try {
      const response = await fetch(attachment.url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (fetchError) {
      console.warn("Fetch download failed, falling back to window.open:", fetchError);
      window.open(attachment.url, '_blank');
    }
  } catch (error) {
    console.error("An error occurred while downloading attachment: ", error);
    if (onError) {
      onError("Failed to download attachment");
    }
  }
};

const escapeHtml = (str) => {
  const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return str.replace(/[&<>"']/g, (ch) => map[ch] || ch);
};

export const renderMentions = (text, mentionedUsers) => {
  // Render @here / @everyone mentions (only in text content, not inside HTML tags)
  text = text.replace(/(<[^>]*>)|([^<]+)/g, (match, tag, content) => {
    if (tag) return tag;
    return content.replace(/(^|[\s,.:;!?])(@(?:here|everyone))\b/gi, (m, prefix, mention) => {
      return `${prefix}<span class="mention-link" style="color: var(--link-color); font-weight: 500;">${mention}</span>`;
    });
  });

  // Render @user mentions
  if (mentionedUsers && mentionedUsers.length > 0) {
    mentionedUsers.forEach((user) => {
      const userName = user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
      if (!userName) return;

      const mentionPattern = new RegExp(
        `@${userName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
        "gi",
      );

      text = text.replace(mentionPattern, (match) => {
        const safeUserId = Number(user.id) || 0;
        const escaped = escapeHtml(match);
        return `<span class="mention-link" data-user-id="${safeUserId}" style="color: var(--link-color); cursor: pointer; font-weight: 500;">${escaped}</span>`;
      });
    });
  }

  return text;
};
